/**
 * Layer 1 — the printed base map: one quad covering the sheet, shaded by TERRAIN_FRAG from the
 * baked field textures. Static per map; per frame only the zoom uniforms change.
 */
import type { GameMap, HexGrid } from '@friction/engine';
import { Geometry, Mesh, type Shader, type Texture } from 'pixi.js';
import type { TerrainFields } from '../bake/terrainFields';
import { destroyMesh, makeDataTexture, makeShader } from '../gfx/gl';
import { TERRAIN_FRAG, WORLD_QUAD_VERT } from '../shaders/terrain';
import type { Rect, SheetLayout } from '../sheet';

/** Sea level in the elevation byte (see GameMap.elevation). */
export const SEA_LEVEL = 64 / 255;

/** A world-space rectangle with only `aPosition` (the map shaders derive everything from it). */
export function quadGeometry(r: Rect): Geometry {
  return new Geometry({
    attributes: {
      aPosition: {
        buffer: new Float32Array([r.x0, r.y0, r.x1, r.y0, r.x1, r.y1, r.x0, r.y1]),
        format: 'float32x2',
      },
    },
    indexBuffer: new Uint32Array([0, 1, 2, 0, 2, 3]),
  });
}

/** Move a quadGeometry to a new rectangle. */
export function setQuad(g: Geometry, r: Rect): void {
  g.getBuffer('aPosition').data = new Float32Array([r.x0, r.y0, r.x1, r.y0, r.x1, r.y1, r.x0, r.y1]);
}

const rectVec = (r: Rect) => new Float32Array([r.x0, r.y0, r.x1, r.y1]);

/** GPU copies of the baked fields, shared by the terrain and overlay shaders. */
export interface FieldTextures {
  a: Texture;
  b: Texture;
  c: Texture;
  bake: Float32Array;
  bakeSize: Float32Array;
  destroy(): void;
}

export function uploadFields(f: TerrainFields): FieldTextures {
  const a = makeDataTexture({
    width: f.width,
    height: f.height,
    data: f.a,
    format: 'rgba16float',
    linear: true,
  });
  const b = makeDataTexture({ width: f.width, height: f.height, data: f.b, linear: true });
  const c = makeDataTexture({ width: f.width, height: f.height, data: f.c, linear: true });
  return {
    a,
    b,
    c,
    bake: new Float32Array([f.originX, f.originY, 1 / (f.width * f.texel), 1 / (f.height * f.texel)]),
    bakeSize: new Float32Array([f.width, f.height]),
    destroy() {
      a.destroy(true);
      b.destroy(true);
      c.destroy(true);
    },
  };
}

export class TerrainLayer {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly shader: Shader;

  constructor(
    _map: GameMap,
    _grid: HexGrid,
    sheet: SheetLayout,
    fields: FieldTextures,
    noise: Texture,
    paper: Texture,
  ) {
    this.shader = makeShader('friction-terrain', WORLD_QUAD_VERT, TERRAIN_FRAG, {
      uFieldA: fields.a.source,
      uFieldB: fields.b.source,
      uFieldC: fields.c.source,
      uNoise: noise.source,
      uPaper: paper.source,
      terrainUniforms: {
        uBake: { value: fields.bake, type: 'vec4<f32>' },
        uBakeSize: { value: fields.bakeSize, type: 'vec2<f32>' },
        uNeat: { value: rectVec(sheet.neat), type: 'vec4<f32>' },
        uFrame: { value: rectVec(sheet.frame), type: 'vec4<f32>' },
        uSheet: { value: rectVec(sheet.sheet), type: 'vec4<f32>' },
        uZoom: { value: 1, type: 'f32' },
        uZoomCss: { value: 1, type: 'f32' },
        uSea: { value: SEA_LEVEL, type: 'f32' },
      },
    });
    this.mesh = new Mesh({ geometry: quadGeometry(sheet.mesh), shader: this.shader });
  }

  update(zoomCss: number, resolution: number): void {
    const u = this.shader.resources.terrainUniforms.uniforms;
    u.uZoom = zoomCss * resolution;
    u.uZoomCss = zoomCss;
  }

  destroy(): void {
    destroyMesh(this.mesh);
  }
}
